"use client";

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import Link from "next/link";
import { useAudioPlayerContext } from "@/contexts/audio-player-context";
import { useAudioUrl } from "@/hooks/useAudioUrl";
import { useIntentPreload } from "@/hooks/useIntentPreload";
import type { Sound } from "@/lib/supabase";
import { cleanSoundTitle, cn } from "@/lib/utils";
import { TESLA_REQUIREMENTS } from "@/lib/audio-utils";
import { soundUrl } from "@/lib/slugify";
import { playbackAudioSource } from "@/lib/sound-audio-source";
import { useFavoriteSounds } from "@/hooks/useFavoriteSounds";
import {
  trackSavedSoundSaveClick,
  trackSavedSoundUnsaveClick,
  trackSavedSoundsPlayClick,
  trackAudioPlay,
  trackSoundDetailClick,
  trackPreviewPlay,
} from "@/lib/tracking";

// Extracted sound-card units
import { useEngagementTracking } from "@/components/ui/sound-card/use-engagement-tracking";
import { useCardDownload } from "@/components/ui/sound-card/use-card-download";
import { useFitTitle } from "@/components/ui/sound-card/use-fit-title";
import { WaveformBand } from "@/components/ui/sound-card/waveform-band";
import { CardActions } from "@/components/ui/sound-card/card-actions";
import { RowReceipt } from "@/components/ui/sound-card/row-receipt";
import { StaticSoundRow, RowDescriptor } from "@/components/ui/sound-card/static-row";
import { RowMeta, rowMetaTokens } from "@/components/ui/sound-card/row-meta";
import { previewFailureMessage } from "@/lib/state-copy";
import { clearRowError, reportRowError } from "@/lib/row-error-store";

// ── Props ──────────────────────────────────────────────────────────────────

export interface SoundCardProps {
  sound: Sound;
  className?: string;
  showCategory?: boolean;
  disableEntryAnimation?: boolean;
  /**
   * Defer the per-card audio URL resolution (useAudioUrl, which feeds the
   * hover/press preload in useIntentPreload) until the card actually scrolls
   * into view, even when `disableEntryAnimation` keeps the card visually
   * rendered from first paint. This keeps the card visible immediately (no
   * CLS) while moving 20+ cards' worth of audio hydration work off the
   * critical path — the dominant TBT cost on /sounds (TES-855).
   */
  deferAudioUntilVisible?: boolean;
  interactionSource?: "default" | "saved_page";
  keyboardShortcut?: number;
  /** Compatibility token for a caller-owned waveform role. Defaults to the one blue brand role. */
  accentToken?: string;
  /**
   * §04.1 density axis. Omit for d1 (default index, 68px/44px/27px) or d0
   * (mobile, handled automatically by the ≤639px breakpoint). Pass "d2" to
   * opt a row into the compact density (56px/40px/19px) for a secondary or
   * related list where more rows need to fit without becoming a grid.
   * Density only changes the resting row's scale — a playing row always
   * expands to the full plate regardless of density (Rule 2).
   */
  density?: "d2";
  /**
   * Related-list form (sound detail page): the title's descriptor shows as a
   * second line, the count reads "1,005 installs" and is omitted under the
   * display floor, and the row carries no Save heart.
   */
  related?: boolean;
}

// ── Lazy loading hook ──────────────────────────────────────────────────────

const useLazyLoading = () => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Once the card has loaded there is nothing left to observe — skip
    // creating a new observer so each card observes at most once.
    if (hasLoaded) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          setHasLoaded(true);
        }
      },
      { rootMargin: "50px", threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [hasLoaded]);

  return { ref, isIntersecting, hasLoaded };
};

// ── Component ──────────────────────────────────────────────────────────────

type RowPlayback = Pick<ReturnType<typeof useAudioPlayerContext>,
  "isPlaying" | "isLoading" | "currentTime" | "duration" | "progress" | "error" | "play" | "pause" | "seek"
> & { active: boolean };

const SoundCard: React.FC<SoundCardProps> = React.memo((props) => {
  const player = useAudioPlayerContext();
  const active = player.currentSound?.id === props.sound?.id;
  const isPlaying = active && player.isPlaying;
  const isLoading = active && player.isLoading;
  const currentTime = active ? player.currentTime : 0;
  const duration = active ? player.duration : 0;
  const progress = active ? player.progress : 0;
  const error = active ? player.error : null;
  const { play, pause, seek } = player;
  // Playback ticks belong to the active row; other rows keep their hook state.
  const playback = useMemo(() => ({
    active, isPlaying, isLoading, currentTime, duration, progress, error, play, pause, seek,
  }), [active, isPlaying, isLoading, currentTime, duration, progress, error, play, pause, seek]);
  return <SoundCardContent {...props} playback={playback} />;
});

const SoundCardContent: React.FC<SoundCardProps & { playback: RowPlayback }> = React.memo(
  ({
    sound,
    className = "",
    showCategory = true,
    disableEntryAnimation = false,
    deferAudioUntilVisible = false,
    interactionSource = "default",
    keyboardShortcut,
    accentToken,
    density,
    related = false,
    playback: globalPlayer,
  }) => {
    const reactId = React.useId();
    const titleId = `sound-card-title-${reactId}`;
    const cardRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    // §12.4 row→plate FLIP: prefers-reduced-motion skips the shared-element
    // transform entirely — the plate renders in its final position at 0ms
    // (§12.10's row for "Row → plate FLIP").
    const prefersReducedMotion = useReducedMotion();

    const { isFavorite, toggleFavorite, hasHydrated } = useFavoriteSounds();
    const isSaved = hasHydrated && isFavorite(sound.id);

    const base = soundUrl(sound);
    const detailHref = interactionSource === "saved_page" ? `${base}?from=saved` : base;

    const isThisSoundActive = globalPlayer.active;
    const isThisSoundPlaying = isThisSoundActive && globalPlayer.isPlaying;
    const isThisSoundLoading = isThisSoundActive && globalPlayer.isLoading;
    useFitTitle(titleRef, cleanSoundTitle(sound.title), isThisSoundActive);

    const needsTrimming = (sound?.duration || 0) > TESLA_REQUIREMENTS.MAX_DURATION;
    const metaTokens = rowMetaTokens({
      downloads: sound?.downloads ?? 0,
      duration: sound?.duration,
      needsTrimming,
      related,
    });

    const { ref: lazyRef, isIntersecting, hasLoaded } = useLazyLoading();
    const shouldRenderFully = disableEntryAnimation || isIntersecting || hasLoaded;
    // Visual render can be immediate (no CLS) while audio resolution stays
    // visibility-gated. When deferAudioUntilVisible, the audio subsystem only
    // engages once the card intersects — even though disableEntryAnimation
    // keeps it painted. Playback itself is unaffected (the global audio
    // player context resolves its own URL on play).
    const shouldLoadAudio = deferAudioUntilVisible
      ? isIntersecting || hasLoaded
      : shouldRenderFully;

    // Observe the live card element so visibility is tracked even when the
    // card renders fully from first paint (skeleton branch is skipped).
    const setCardRootRef = useCallback(
      (node: HTMLDivElement | null) => {
        cardRef.current = node;
        (lazyRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      },
      [lazyRef]
    );

    // The same playback source the global player resolves (the compressed
    // preview when there is one), so a buffered element is handed straight to
    // the player instead of being fetched a second time under another URL.
    const playbackSource = playbackAudioSource(sound);
    const { audioUrl } = useAudioUrl(
      shouldLoadAudio ? playbackSource.fileKey : null,
      shouldLoadAudio ? playbackSource.resolvedUrl : null
    );

    // Audio is fetched on intent (hover or press of the play control), never
    // because the row became visible: each fetch is billed as Supabase
    // Storage egress, and most rows a visitor scrolls past are never played.
    const intentHandlers = useIntentPreload(sound?.id, audioUrl);

    // TES-829 + W3-1: one-shot engaged-preview / preview-completed flags,
    // derived from the global playback clock (only meaningful while this
    // sound is the active one).
    const { hasEngagedPreview, previewCompleted } = useEngagementTracking({
      isThisSoundPlaying,
      currentTime: globalPlayer.currentTime,
      duration: globalPlayer.duration,
      progress: globalPlayer.progress,
    });

    // Card download state + handler (tracking → increment → download order).
    const {
      isDownloading,
      downloadDone,
      downloadError,
      receiptOpen,
      dismissReceipt,
      handleCardDownload,
      handleShare,
    } = useCardDownload(sound, previewCompleted);

    // Closing the receipt unmounts the button that had focus, so hand focus
    // back to the row's Download button rather than dropping it to <body>.
    const handleDismissReceipt = useCallback(() => {
      dismissReceipt();
      cardRef.current?.querySelector<HTMLButtonElement>(".sc-download")?.focus();
    }, [dismissReceipt]);

    // Download and preview failures share one slot (lib/row-error-store.ts), so
    // a stale note never hides a live one and two rows never both show one.
    const errorMessage = downloadError;
    const playerError = isThisSoundActive ? globalPlayer.error : null;
    useEffect(() => {
      if (playerError) reportRowError(sound.id, previewFailureMessage(playerError));
    }, [playerError, sound.id]);
    const errorNoteRef = useRef<HTMLParagraphElement>(null);
    // The active row is a tall plate on a phone, so the note can land below the
    // fold. Bring it in only when it is out of view; scroll-padding on <html>
    // already keeps it clear of the nav and the docked player.
    useEffect(() => {
      if (errorMessage) errorNoteRef.current?.scrollIntoView?.({ block: "nearest" });
    }, [errorMessage]);

    // ── Handlers ─────────────────────────────────────────────────────────

    const handlePlayPause = useCallback(
      (e?: React.MouseEvent) => {
        e?.stopPropagation();
        // Loading counts as "engaged" so a second activation during the load
        // cancels it rather than re-issuing play(). The band's play control is
        // no longer `disabled` while loading (TES-1581), so this keeps it a
        // strict toggle — matching the detail-page player (TES-1579).
        if (isThisSoundPlaying || isThisSoundLoading) {
          globalPlayer.pause();
        } else {
          clearRowError();
          globalPlayer.play(sound);
          trackAudioPlay({ sound_id: sound.id, source: "sound_card", autoplay: false });
          trackPreviewPlay({
            sound_id: sound.id,
            sound_title: sound.title,
            category: sound.category,
            source: "sound_card",
          });
          if (interactionSource === "saved_page") trackSavedSoundsPlayClick(sound.id);
        }
      },
      [isThisSoundPlaying, isThisSoundLoading, globalPlayer, sound, interactionSource]
    );

    const handleFavoriteToggle = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        const saved = toggleFavorite(sound.id);
        if (saved) {
          trackSavedSoundSaveClick({ soundId: sound.id, source: "sound_card" });
        } else {
          trackSavedSoundUnsaveClick({ soundId: sound.id, source: "sound_card" });
        }
      },
      [toggleFavorite, sound.id]
    );

    // ── Error / skeleton states ───────────────────────────────────────────

    if (!sound) {
      return (
        <div
          className="border-y p-4"
          style={{ borderColor: "var(--tls-border-subtle)", background: "var(--tls-surface)" }}
        >
          <div className="text-center text-sm" style={{ color: "var(--tls-fg-muted)" }}>
            Sound data not available
          </div>
        </div>
      );
    }

    if (!sound.id) {
      return (
        <div
          className="border-y p-4"
          style={{ borderColor: "var(--tls-border-subtle)", background: "var(--tls-surface)" }}
        >
          <div className="text-center text-sm" style={{ color: "var(--tls-fg-muted)" }}>
            Sound ID missing
          </div>
        </div>
      );
    }

    // Before the card intersects — which on the server is always, since both
    // flags start false — the row still has to carry its link, title and
    // category. A content-free placeholder here costs the server HTML every
    // row a server component renders: 16 category hubs, /lockchime, /download
    // and /sounds/model/* each shipped a stack of skeletons and zero
    // crawlable detail links (TES-2361). The windowed grids
    // (sound-grid.tsx, sort-grid.tsx) already fixed this for themselves by
    // routing their deferred slots to StaticSoundRow after the same
    // regression cost a 164-sound collection page all but 6 links (P0-1,
    // 2026-09-03); this is that fix moved down into the component so every
    // caller gets it. Client-side lazy hydration is unchanged — only what the
    // pre-intersection paint contains (TES-834, TES-1077).
    if (!shouldRenderFully) {
      return (
        <div ref={lazyRef}>
          <StaticSoundRow
            sound={sound}
            showCategory={showCategory}
            density={density}
            related={related}
          />
        </div>
      );
    }

    // ── Main render ───────────────────────────────────────────────────────

    return (
      <>
        <article
          ref={setCardRootRef}
          aria-labelledby={titleId}
          data-testid="sound-card"
          data-row-density={density}
          className={cn(
            "sc-marquee",
            isThisSoundActive && "is-active",
            isThisSoundPlaying && "is-playing",
            className
          )}
          style={{
            // The waveform carries the one shared blue accent. Callers can keep
            // an explicit legacy role during migration; all exposed roles resolve
            // to the same blue in the Plate & Hairline token layer.
            ["--sc-cat" as string]: accentToken ? `var(${accentToken})` : "var(--tls-c-brand)",
          }}
        >
          <WaveformBand
            // Stored peaks only (LIST_COLUMNS carries them). A row never decodes
            // its file for a waveform; a peakless row draws an empty strip.
            peaks={sound.waveform_data}
            currentTime={isThisSoundActive ? globalPlayer.currentTime || 0 : 0}
            // The catalog length first, so the readout does not change when the
            // element reports its decoded length, as on the hero.
            duration={sound.duration || (isThisSoundActive ? globalPlayer.duration : 0) || 0}
            isPlaying={isThisSoundPlaying}
            isLoading={isThisSoundLoading}
            title={sound.title}
            onPlayPause={handlePlayPause}
            onSeek={globalPlayer.seek}
            seekable={!errorMessage}
            expanded={isThisSoundActive}
            intentHandlers={intentHandlers}
          />

          {/* ── Body ─────────────────────────────────────────────────────── */}
          {/* DS SoundRow anatomy, placed by grid-template-areas in globals.css:
              the name, the category and facts line under it, controls. The
              resting state is a compact index row; the active root owns the
              larger plate, instead of making every result a tile. */}
          <div className="sc-content">
            {/* The name wraps rather than truncates, and it claims the plate
                when its own preview becomes active. */}
            <Link prefetch={false}
              href={detailHref}
              onClick={e => {
                e.stopPropagation();
                trackSoundDetailClick({
                  sound_id: sound.id,
                  sound_title: sound.title,
                  source: interactionSource,
                });
              }}
              // Desktop-pointer hit-area gap: the painted title measures
              // ~29.7px tall (one text line) against the row model's 44px
              // floor. Same pattern as the nav's route pills — a padded
              // ::before target, not bigger type (PRODUCT.md: "the 44px
              // target is a padded wrapper, not a taller button"). Gated to
              // `sm:` (640px) and up: below that, `.sc-title` wraps to two
              // lines and the row's own min-height already clears 44px, so
              // expanding further would eat into the ~16px margin above/below
              // and risk overlapping the neighbouring row's target. At
              // desktop widths the row's min-height (68px) leaves ~19px of
              // margin per side around the 29.7px link box, so an 8px
              // inset-y expansion (→ ~45.7px) has ~11px of clearance left
              // before it would reach the hairline border, let alone the
              // next row's own target.
              className="sc-title-link relative before:absolute before:inset-x-0 before:content-[''] sm:before:-inset-y-2"
              style={{ textDecoration: "none", display: "block", minWidth: 0 }}
            >
              <motion.h3
                id={titleId}
                ref={titleRef}
                className="sc-title"
                // §12.4 — the row IS the plate's expanded state (Rule 2), so
                // this is the same DOM node changing size via the .is-active
                // class, not two different nodes swapping. `layout` alone
                // (no layoutId — there is nothing else to match against)
                // gives Framer the before/after rects to FLIP between.
                // Gated to the active row only: an inactive row's title
                // never opts into layout tracking, so Framer never has to
                // measure or project a rect for it — the 1,400-row cost the
                // spec's flipRules warn about is paid by at most one row at
                // a time.
                layout={isThisSoundActive ? "position" : false}
                transition={
                  prefersReducedMotion
                    ? { duration: 0 }
                    : { duration: 0.22, ease: [0.2, 0, 0, 1] }
                }
                style={{
                  // Size, weight and tracking live in .sc-title, NOT here.
                  // Rule 2 steps them up when the row is playing, and an inline
                  // style outranks any class rule, so setting them here made
                  // the expansion silently do nothing but change case.
                  margin: 0,
                  fontFamily: "var(--tls-font-display)",
                  color: "var(--tls-fg-strong)",
                }}
              >
                {cleanSoundTitle(sound.title)}
              </motion.h3>
            </Link>

            {related && <RowDescriptor title={sound.title} />}

            <RowMeta
              tokens={metaTokens}
              category={showCategory ? sound.category : null}
              isPlaying={isThisSoundPlaying}
              soundId={sound.id}
              onCategoryClick={e => e.stopPropagation()}
            />

            <CardActions
              title={sound.title}
              hasEngagedPreview={hasEngagedPreview}
              previewCompleted={previewCompleted}
              isDownloading={isDownloading}
              downloadDone={downloadDone}
              isSaved={isSaved}
              keepSaveOnMobile={interactionSource === "saved_page"}
              onDownload={handleCardDownload}
              onFavoriteToggle={handleFavoriteToggle}
              onShare={handleShare}
              related={related}
            />
          </div>

          {/* A failure is said where it happened, in the row, in words (§09), and
              it is the same sentence the live region carries. */}
          {errorMessage && (
            <p
              ref={errorNoteRef}
              data-testid="sound-card-error"
              className="m-0 pb-3 text-sm font-bold"
              // The active row is an inverted plate (fg-strong ground), so the
              // note takes the canvas ink there, the same as the title.
              style={{
                gridColumn: isThisSoundActive ? 1 : 2,
                color: isThisSoundActive ? "var(--tls-canvas)" : "var(--tls-fg-strong)",
              }}
            >
              {errorMessage}
            </p>
          )}
        </article>
        {/* After a download the row continues into its install receipt, as a
            sibling so it sits on the page ground even when the row above it is
            the inverted plate. */}
        {receiptOpen && (
          <RowReceipt soundId={sound.id} title={sound.title} onDismiss={handleDismissReceipt} />
        )}
      </>
    );
  }
);

SoundCard.displayName = "SoundCard";
SoundCardContent.displayName = "SoundCardContent";

export default SoundCard;
